import { FC, useContext, useEffect, useState } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import styled, { useTheme } from 'styled-components'
import {
  Body2,
  Capitalized,
  MediumTinyBold,
  Tiny2
} from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import FormInput from '@/components/molecules/form/formInput'
import FormElement from '@/components/molecules/form/formElement'
import { GenderRadioBox } from '@/components/molecules/radioButton'
import TitledCheckbox from '@/components/molecules/titledCheckbox'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'
import {
  getValidationResult,
  initData,
  Input,
  InputValue,
  isFormValid,
  onInputChange
} from '@/components/molecules/form/form'
import {
  makeRegistrationInputFields,
  RegistrationField
} from '@/components/organisms/registration/registrationFormData'
import ErrorText from '@/components/atoms/errorText'
import { CustomerGender, Sdk } from '@/network/graphql.g'
import Trans from 'next-translate/Trans'
import useGraphQl from 'hooks/useGraphQl'
import { useRouter } from 'next/router'
import { BigBlackButton } from '@/components/atoms/button'
import { useMhub } from '@/providers/mhubProvider'
import { LoginType } from '@/providers/mhub/userManager'

import { useSessionStorage } from 'hooks/useSessionStorage'
import LoginSocialButtons from '../login/loginSocialButtons'
import { StoreContext } from '@/providers/storeProvider'
import { useAuth } from '@/providers/authProvider'

type RegistrationFormProps = {
  title?: string
  buttonLabel?: string
  isSignUp?: boolean
  isCheckoutSuccessRegistrationForm?: boolean
}

const RegistrationForm: FC<RegistrationFormProps> = ({
  title,
  buttonLabel,
  isSignUp = true,
  isCheckoutSuccessRegistrationForm
}) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const [gender, setGender] = useState<CustomerGender>(null)
  const fieldsData: Input[] = makeRegistrationInputFields(t)
  const [confirmConditions, setConfirmConditions] = useState<boolean>(false)
  const [confirmNewsletter, setConfirmNewsletter] = useState<boolean>(false)
  const [showValidation, setShowValidation] = useState<boolean>(false)
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
  const [registrationData, setRegistrationData] = useState<
    Record<string, InputValue>
  >({})
  const { requestNotifySuccessError } = useGraphQl()
  const router = useRouter()
  const { pushRegisterEvent } = useMhub()
  const { getStoreSession, removeStoreSession } = useSessionStorage()
  const { loginByCredentials } = useAuth()

  useEffect(() => {
    if (router.pathname === '/checkout-success') {
      const {
        customerFirstname: name,
        customerLastname: surname,
        customerEmail: email
      } = (getStoreSession('clientFormData') as { [key: string]: string }) || {}

      const valueFieldsData = []
      Object.entries({ name, surname, email }).forEach(([key, value]) => {
        const currentFieldElement = fieldsData.find((el) => el.name === key)
        if (currentFieldElement) {
          const copyElement = { ...currentFieldElement }
          copyElement.initialValue = value
          valueFieldsData.push(copyElement)
        }
      })
      initData(valueFieldsData, setRegistrationData)
    } else {
      initData(fieldsData, setRegistrationData)
    }
  }, [])

  const validateForms = async (e) => {
    e.preventDefault()
    setShowValidation(true)
    const formIsValid = isFormValid(registrationData)
    if (formIsValid && confirmConditions && gender) {
      setRequestInProgress(true)

      const result = await requestNotifySuccessError((sdk: Sdk) => ({
        method: sdk.register,
        variables: {
          basicInfo: {
            email: registrationData[RegistrationField.email].value,
            gender,
            firstname: registrationData[RegistrationField.name].value,
            lastname: registrationData[RegistrationField.surname].value
          },
          newsletterAgree: confirmNewsletter,
          termsAgree: confirmConditions,
          password: registrationData[RegistrationField.password].value,
          orderId: +router.query.id
        },
        successText: () => t('Register.successText')
      }))
      if (result.success) {
        pushRegisterEvent(
          isCheckoutSuccessRegistrationForm
            ? LoginType.CheckouSuccess
            : LoginType.Direct,
          {
            email: result.response?.registration?.email,
            rs: result.response?.registration?.rs,
            ts: result.response?.registration?.ts
          }
        )
        loginByCredentials(
          registrationData[RegistrationField.email].value,
          registrationData[RegistrationField.password].value
        )
      }
      setRequestInProgress(false)
      removeStoreSession('clientFormData')
    }
  }

  const formTitle = title ?? t('Register.Email.title')
  const buttonTitle = buttonLabel ?? t('Register.Email.action')

  const { isAliatic } = useContext(StoreContext)

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="center"
        width={{ mobile: '90%', tablet: 500, desktop: 500 }}
        p={{ mobile: 12, tablet: 0, desktop: 0 }}
      >
        {formTitle ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            width="100%"
            mt={40}
            mb={20}
            mx={33}
          >
            <StyledCapitalized px={15}>{formTitle}</StyledCapitalized>
          </Flex>
        ) : null}
        <StyledForm onSubmit={validateForms}>
          <Box width="100%">
            {fieldsData.map(
              ({ name, subTitle, placeholder, rules, valueType, type }) => {
                const { isValid, errorMessage } = getValidationResult(
                  registrationData[name],
                  rules
                )
                return (
                  <Box key={name}>
                    <FormElement
                      showValidation={showValidation}
                      isValid={isValid}
                    >
                      <FormInput
                        name={name}
                        value={registrationData[name]?.value}
                        width="100%"
                        placeholder={placeholder}
                        isError={showValidation && !isValid}
                        type={valueType}
                        formInputType={type}
                        onChange={(name, value) => {
                          onInputChange(name, value, rules, setRegistrationData)
                        }}
                        errorPlaceholder={errorMessage}
                      />
                    </FormElement>
                    {subTitle && (
                      <Tiny2
                        m="0 0 20px 0"
                        color={theme.colors.inputPlaceholder}
                      >
                        {subTitle}
                      </Tiny2>
                    )}
                  </Box>
                )
              }
            )}
          </Box>
          <Flex
            flexDirection="column"
            justifyContent="start"
            width="100%"
            my={20}
          >
            {showValidation && !gender && (
              <ErrorText>
                {t('Register.Email.Gender.noGenderErrorPlaceholder')}
              </ErrorText>
            )}
            <Flex alignItems="center">
              <Body2 my="5px" mr={14}>
                {t('Register.Email.Gender.title')}
              </Body2>
              <GenderRadioBox
                value={CustomerGender.Male}
                onChanged={setGender}
                title={t('Common.male')}
                mr={24}
                isChecked={gender === CustomerGender.Male}
              />
              <GenderRadioBox
                value={CustomerGender.Female}
                onChanged={setGender}
                isChecked={gender === CustomerGender.Female}
                title={t('Common.female')}
              />
            </Flex>
          </Flex>
          <StyledMediumTinyBold
            color={theme.colors.errorSecondary}
            textAlign="left"
            m={0}
            opacity={showValidation && !confirmConditions ? 1 : 0}
          >
            {t('Register.Email.conditionsCheckErrorPlaceholder')}
          </StyledMediumTinyBold>
          <TitledCheckbox
            data-testid="confirmConditions"
            isChecked={confirmConditions}
            onChanged={(value) => setConfirmConditions(value)}
            isValid={showValidation ? confirmConditions : true}
            TitleElement={() => (
              <Flex width="100%">
                <Capitalized
                  m={0}
                  onClick={() => setConfirmConditions((value) => !value)}
                >
                  {t('Register.Email.conditionsCheck1')}
                </Capitalized>
                <Capitalized my={0} mx={1}>
                  <Link href={Routes.conditions} textDecoration={'underline'}>
                    {t('Register.Email.conditionsCheck2')}
                  </Link>
                </Capitalized>
              </Flex>
            )}
          />
          <TitledCheckbox
            data-testid="confirmNewsletter"
            isChecked={confirmNewsletter}
            onChanged={(value) => setConfirmNewsletter(value)}
            TitleElement={() => (
              <Capitalized
                m={0}
                onClick={() => setConfirmNewsletter((value) => !value)}
              >
                {t('Register.Email.newsletter')}
              </Capitalized>
            )}
          />
          <BigBlackButton
            isDisabled={requestInProgress}
            mt={30}
            mb={30}
            backgroundColor={theme.colors.onBackground}
            onClick={validateForms}
            data-testid="registerButton"
            title={buttonTitle}
            width="100%"
            type="submit"
          />
        </StyledForm>

        {isSignUp ? (
          <Capitalized display="inline-block" m={0}>
            <Trans
              i18nKey="common:Register.Email.login"
              components={[
                <span />,
                <Link href={Routes.login} textDecoration={'underline'} />
              ]}
            />
          </Capitalized>
        ) : null}

        {!isAliatic && <LoginSocialButtons />}
      </Flex>
    </>
  )
}

export default RegistrationForm

const StyledCapitalized = styled(Capitalized)`
  text-transform: uppercase;
  margin: 0;
  background-color: ${(props) => props.theme.colors.background};
`

const StyledMediumTinyBold = styled(MediumTinyBold)`
  min-height: 13px;
  width: 100%;
`

const StyledForm = styled.form`
  width: 100%;
  position: relative;
`
